import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import routes from 'src/routes'
import useBreadcrumbNameResolvers from 'src/hooks/useBreadcrumbNameResolvers'

import { CBreadcrumb, CBreadcrumbItem } from '@coreui/react'

const AppBreadcrumb = () => {
  const location = useLocation()
  const appRoutePrefix = '/app'
  const breadcrumbNameResolvers = useBreadcrumbNameResolvers()

  const [resolvedNames, setResolvedNames] = useState({})

  const currentLocation = location.pathname

  const normalizePath = (path) => {
    if (!path) return '/'

    const normalized = path.replace(/\/+/g, '/').replace(/\/$/, '')

    return normalized || '/'
  }

  const splitPath = (path) => {
    return normalizePath(path).split('/').filter(Boolean)
  }

  const isDynamicSegment = (segment) => {
    return segment?.startsWith(':')
  }

  const isGuid = (value) => {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
  }

  const shouldDynamicSegmentMatch = (routeSegment, pathnameSegment) => {
    if (!isDynamicSegment(routeSegment)) return false

    const paramName = routeSegment.substring(1)

    // Prevent routes like /products/:productId from accidentally matching
    // /products/edit where "edit" gets treated as the productId.
    if (paramName.toLowerCase().endsWith('id')) {
      return isGuid(pathnameSegment)
    }

    return true
  }

  const getRouteMatch = (pathname, routes) => {
    const pathnameSegments = splitPath(pathname)
    const possibleMatches = []

    for (const route of routes) {
      if (!route.path || route.path === '*') continue

      const routePathsToTry = [`${appRoutePrefix}${route.path}`, route.path]

      for (const routePathToTry of routePathsToTry) {
        const routeSegments = splitPath(routePathToTry)

        if (pathnameSegments.length !== routeSegments.length) continue

        const params = {}
        let isMatch = true
        let dynamicSegmentCount = 0

        for (let index = 0; index < routeSegments.length; index++) {
          const routeSegment = routeSegments[index]
          const pathnameSegment = pathnameSegments[index]

          if (isDynamicSegment(routeSegment)) {
            if (!shouldDynamicSegmentMatch(routeSegment, pathnameSegment)) {
              isMatch = false
              break
            }

            const paramName = routeSegment.substring(1)
            params[paramName] = pathnameSegment
            dynamicSegmentCount += 1
            continue
          }

          if (routeSegment !== pathnameSegment) {
            isMatch = false
            break
          }
        }

        if (isMatch) {
          possibleMatches.push({
            route,
            params,
            dynamicSegmentCount,
          })
        }
      }
    }

    if (possibleMatches.length === 0) return null

    // Prefer routes with fewer dynamic segments.
    // This helps static routes win over broad dynamic routes.
    return possibleMatches.sort((a, b) => a.dynamicSegmentCount - b.dynamicSegmentCount)[0]
  }

  const getPathSegmentsForBreadcrumbs = (pathname) => {
    const segments = splitPath(pathname)

    if (segments[0]?.toLowerCase() === appRoutePrefix.replace('/', '').toLowerCase()) {
      return {
        startingPathname: appRoutePrefix,
        segments: segments.slice(1),
        linkPrefix: appRoutePrefix,
      }
    }

    return {
      startingPathname: '',
      segments,
      linkPrefix: '',
    }
  }

  const buildPathFromRoute = (routePath, params, linkPrefix) => {
    const pathSegments = splitPath(routePath)

    const resolvedSegments = pathSegments.map((segment) => {
      if (!isDynamicSegment(segment)) return segment

      const paramName = segment.substring(1)
      return params[paramName] ?? segment
    })

    return normalizePath(`${linkPrefix}/${resolvedSegments.join('/')}`)
  }

  const getDynamicBreadcrumbConfig = (route, params) => {
    if (route.breadcrumbName?.resolver && route.breadcrumbName?.param) {
      const resolverId = params[route.breadcrumbName.param]

      if (resolverId) {
        return {
          route,
          resolverName: route.breadcrumbName.resolver,
          resolverParam: route.breadcrumbName.param,
          resolverId,
        }
      }
    }

    return null
  }

  const getInheritedDynamicBreadcrumbConfig = (currentRoute, params) => {
    const matchingRoute = routes.find((route) => {
      if (!route.path || route.path === '*' || route === currentRoute) return false

      if (!route.breadcrumbName?.resolver || !route.breadcrumbName?.param) return false

      const paramName = route.breadcrumbName.param
      const paramValue = params[paramName]

      if (!paramValue) return false

      if (paramName.toLowerCase().endsWith('id') && !isGuid(paramValue)) {
        return false
      }

      return true
    })

    if (!matchingRoute) return null

    const resolverParam = matchingRoute.breadcrumbName.param
    const resolverId = params[resolverParam]

    return {
      route: matchingRoute,
      resolverName: matchingRoute.breadcrumbName.resolver,
      resolverParam,
      resolverId,
    }
  }

  const createDynamicBreadcrumb = ({
    pathname,
    fallbackName,
    active,
    resolverName,
    resolverId,
  }) => {
    const resolveKey = `${resolverName}:${resolverId}`

    return {
      pathname,
      fallbackName,
      active,
      requiresResolvedName: true,
      resolveKey,
      resolverName,
      resolverId,
    }
  }

  const addBreadcrumb = (breadcrumbs, breadcrumb) => {
    const alreadyExists = breadcrumbs.some((existing) => {
      if (breadcrumb.resolveKey && existing.resolveKey === breadcrumb.resolveKey) return true
      return (
        existing.pathname === breadcrumb.pathname &&
        existing.fallbackName === breadcrumb.fallbackName
      )
    })

    if (!alreadyExists) {
      breadcrumbs.push(breadcrumb)
    }
  }
  const breadcrumbDefinitions = useMemo(() => {
    const breadcrumbs = []
    const addedResolveKeys = new Set()

    const { startingPathname, segments, linkPrefix } =
      getPathSegmentsForBreadcrumbs(currentLocation)

    addBreadcrumb(breadcrumbs, {
      pathname: linkPrefix || '/',
      fallbackName: 'Home',
      active: false,
      requiresResolvedName: false,
      resolveKey: null,
      resolverName: null,
      resolverId: null,
    })

    let currentPathname = startingPathname

    segments.forEach((segment) => {
      currentPathname = normalizePath(`${currentPathname}/${segment}`)

      const routeMatch = getRouteMatch(currentPathname, routes)

      if (!routeMatch?.route?.name) return

      const { route, params } = routeMatch

      // Avoid adding Home again if '/' ever gets matched.
      if (route.path === '/') return

      const ownDynamicConfig = getDynamicBreadcrumbConfig(route, params)

      if (ownDynamicConfig) {
        const resolveKey = `${ownDynamicConfig.resolverName}:${ownDynamicConfig.resolverId}`
        addedResolveKeys.add(resolveKey)

        addBreadcrumb(
          breadcrumbs,
          createDynamicBreadcrumb({
            pathname: currentPathname,
            fallbackName: route.name,
            active: false,
            resolverName: ownDynamicConfig.resolverName,
            resolverId: ownDynamicConfig.resolverId,
          }),
        )

        return
      }

      const inheritedDynamicConfig = getInheritedDynamicBreadcrumbConfig(route, params)

      if (inheritedDynamicConfig) {
        const resolveKey = `${inheritedDynamicConfig.resolverName}:${inheritedDynamicConfig.resolverId}`

        if (!addedResolveKeys.has(resolveKey)) {
          addedResolveKeys.add(resolveKey)

          addBreadcrumb(
            breadcrumbs,
            createDynamicBreadcrumb({
              pathname: buildPathFromRoute(inheritedDynamicConfig.route.path, params, linkPrefix),
              fallbackName: inheritedDynamicConfig.route.name,
              active: false,
              resolverName: inheritedDynamicConfig.resolverName,
              resolverId: inheritedDynamicConfig.resolverId,
            }),
          )
        }
      }

      addBreadcrumb(breadcrumbs, {
        pathname: currentPathname,
        fallbackName: route.name,
        active: false,
        requiresResolvedName: false,
        resolveKey: null,
        resolverName: null,
        resolverId: null,
      })
    })

    if (breadcrumbs.length > 0) {
      breadcrumbs.forEach((breadcrumb) => {
        breadcrumb.active = false
      })

      breadcrumbs[breadcrumbs.length - 1].active = true
    }

    return breadcrumbs
  }, [currentLocation])

  useEffect(() => {
    let cancelled = false

    const unresolvedBreadcrumbs = breadcrumbDefinitions.filter(
      (breadcrumb) =>
        breadcrumb.requiresResolvedName &&
        breadcrumb.resolveKey &&
        breadcrumb.resolverName &&
        breadcrumb.resolverId &&
        !resolvedNames[breadcrumb.resolveKey],
    )

    if (unresolvedBreadcrumbs.length === 0) return

    const resolveBreadcrumbNames = async () => {
      const newResolvedNames = {}

      for (const breadcrumb of unresolvedBreadcrumbs) {
        const resolver = breadcrumbNameResolvers[breadcrumb.resolverName]

        if (!resolver) {
          newResolvedNames[breadcrumb.resolveKey] = breadcrumb.fallbackName
          continue
        }

        try {
          const resolvedName = await resolver(breadcrumb.resolverId)

          newResolvedNames[breadcrumb.resolveKey] = resolvedName || breadcrumb.fallbackName
        } catch (error) {
          console.error('Failed to resolve breadcrumb name', {
            breadcrumb,
            error,
          })

          newResolvedNames[breadcrumb.resolveKey] = breadcrumb.fallbackName
        }
      }

      if (!cancelled && Object.keys(newResolvedNames).length > 0) {
        setResolvedNames((previous) => ({
          ...previous,
          ...newResolvedNames,
        }))
      }
    }

    resolveBreadcrumbNames()

    return () => {
      cancelled = true
    }
  }, [breadcrumbDefinitions, breadcrumbNameResolvers, resolvedNames])

  const waitingForResolvedNames = breadcrumbDefinitions.some(
    (breadcrumb) => breadcrumb.requiresResolvedName && !resolvedNames[breadcrumb.resolveKey],
  )

  if (waitingForResolvedNames) {
    return null
  }

  const breadcrumbs = breadcrumbDefinitions.map((breadcrumb) => ({
    pathname: breadcrumb.pathname,
    name: breadcrumb.requiresResolvedName
      ? resolvedNames[breadcrumb.resolveKey]
      : breadcrumb.fallbackName,
    active: breadcrumb.active,
  }))

  return (
    <CBreadcrumb className="p-0 align-items-center mb-0">
      {breadcrumbs.map((breadcrumb, index) => {
        return (
          <CBreadcrumbItem
            {...(breadcrumb.active ? { active: true } : { href: breadcrumb.pathname })}
            key={`${breadcrumb.pathname}-${index}`}
          >
            {breadcrumb.name}
          </CBreadcrumbItem>
        )
      })}
    </CBreadcrumb>
  )
}

export default React.memo(AppBreadcrumb)
